import { redirect, Link } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { Card, CardDescription, CardHeader, CardTitle } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => [
  { title: 'Youth Rollcall | Show up. Do more together.' },
]

export async function loader({ request }: Route.LoaderArgs) {
  const userId = await getUserId(request)

  // Redirect authenticated users to dashboard
  if (userId) {
    throw redirect('/dashboard')
  }

  return null
}

export default function Index() {
  return (
    <>
      <main className="container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto text-center space-y-16">
          {/* Hero Section */}
          <div className="space-y-6">
            <h1 className="font-brand text-4xl font-extrabold tracking-tight md:text-6xl">
              Show up.
              <span className="text-brand block">Do more together.</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Youth Rollcall is attendance for youth programs, built for the phone
              in a teacher's hand. Know who's here in seconds, and keep everyone
              connected.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to="/login">
                  Get Started
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/about">
                  Learn More
                </Link>
              </Button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mt-16 text-left">
            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4">
                  <Icon name="check" className="h-6 w-6 text-brand" />
                </div>
                <CardTitle>Quick Attendance</CardTitle>
                <CardDescription>
                  Take attendance in seconds with our mobile-optimized interface.
                  Large touch targets and one-tap status changes.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4">
                  <Icon name="file-text" className="h-6 w-6 text-brand" />
                </div>
                <CardTitle>Trends at a glance</CardTitle>
                <CardDescription>
                  Track attendance trends, identify patterns, and generate
                  comprehensive reports with export capabilities.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-4">
                  <Icon name="lock-closed" className="h-6 w-6 text-brand" />
                </div>
                <CardTitle>Role-Based Access</CardTitle>
                <CardDescription>
                  Secure access control with different permission levels for
                  admins, program leads, teachers, and viewers.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* Demo Section */}
          <div className="bg-muted border rounded-xl p-10 mt-16">
            <h2 className="text-3xl font-bold mb-4">Perfect for Youth Programs</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Whether you're running after-school programs, summer camps, or leadership
              development initiatives, Youth Rollcall adapts to how you already run things.
            </p>
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div className="space-y-4">
                <h3 className="text-xl font-bold">For Teachers</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Icon name="check" className="h-4 w-4 text-brand" />
                    Mobile-first attendance taking
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="check" className="h-4 w-4 text-brand" />
                    Quick student status updates
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="check" className="h-4 w-4 text-brand" />
                    Class-specific analytics
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-bold">For Program Leaders</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Icon name="check" className="h-4 w-4 text-brand" />
                    Comprehensive program oversight
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="check" className="h-4 w-4 text-brand" />
                    Student enrollment management
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="check" className="h-4 w-4 text-brand" />
                    Export reports and data
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
